
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { User, Lead, CustomFieldDefinition } from '../types';
import { api } from '../services/apiService';
import { toast } from '../services/toast';
import ConfirmModal from './ConfirmModal';

const DEFAULT_REVENUE_BRACKETS = ['<5K', '5-20K', '20-50K', '50-100K', '>100K'];

interface AddLeadModalProps {
  currentUser: User;
  revenueBrackets?: string[];
  customFieldDefinitions?: CustomFieldDefinition[];
  onClose: () => void;
  onSubmit: (leadData: Omit<Lead, 'id' | 'createdAt'>) => Promise<void> | void;
}

const AddLeadModal: React.FC<AddLeadModalProps> = ({ currentUser, revenueBrackets: propBrackets, onClose, onSubmit }) => {
  const effectiveBrackets = (propBrackets && propBrackets.length > 0) ? propBrackets : DEFAULT_REVENUE_BRACKETS;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sectors, setSectors] = useState<string[]>([]);
  const [showCustomRevenue, setShowCustomRevenue] = useState(false);
  // Modale de confirmation pour le doublon détecté à la création.
  const [duplicatePrompt, setDuplicatePrompt] = useState<{ info: string; payload: any } | null>(null);
  const [duplicateSaving, setDuplicateSaving] = useState(false);
  const [formData, setFormData] = useState({
    clientCompanyName: '',
    tag: '',
    sectorActivity: '',
    name: '',
    email: '',
    phone: '',
    phoneLandline: '',
    status: 'new' as Lead['status'],
    additionalInfo: '',
    potentialRevenue: '',
    source: ''
  });

  useEffect(() => {
    api.getActivitySectors().then(list => setSectors(list || [])).catch(() => setSectors([]));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Au moins un des deux : nom complet ou entreprise
    const nameTrim = formData.name.trim();
    const companyTrim = formData.clientCompanyName.trim();
    if (!nameTrim && !companyTrim) {
      toast.warning("Renseignez au moins le nom complet ou le nom de l'entreprise.");
      return;
    }

    // Email optionnel — si renseigné, il doit être valide
    const emailTrim = formData.email.trim();
    if (emailTrim) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailTrim)) {
        toast.warning("Veuillez saisir un email valide.");
        return;
      }
    }

    if (isSubmitting) return; // Éviter les doubles soumissions
    setIsSubmitting(true);

    // Fallback : si pas de nom, on reprend le nom de l'entreprise pour que
    // l'affichage (avatar, listes, kanban) reste cohérent partout.
    const leadData = {
      ...formData,
      name: nameTrim || companyTrim,
      companyId: currentUser.companyId,
      assignedTo: currentUser.id,
      isFavorite: false,
    };

    try {
      // Attendre que la création soit terminée
      await onSubmit(leadData);
      
      // Fermer la modal après succès
      onClose();
      
    } catch (error: any) {
      const msg = error?.message || '';
      if (msg.startsWith('DUPLICATE:')) {
        // On ouvre la modale ConfirmModal stylée (cohérente avec le thème).
        // La création forcée est lancée depuis le handler de confirmation.
        setDuplicatePrompt({ info: msg.replace('DUPLICATE:', ''), payload: leadData });
        setIsSubmitting(false);
      } else {
        console.error("Erreur lors de la creation du prospect:", error);
        // Surface le message exact du serveur quand disponible (validation, RGPD, etc.)
        toast.error(msg || 'Erreur lors de la création du prospect');
        setIsSubmitting(false);
      }
    }
  };

  const confirmDuplicate = async () => {
    if (!duplicatePrompt) return;
    setDuplicateSaving(true);
    try {
      await onSubmit({ ...duplicatePrompt.payload, forceDuplicate: true } as any);
      setDuplicatePrompt(null);
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'Échec de la création (doublon forcé)');
    } finally {
      setDuplicateSaving(false);
    }
  };

  const modalContent = (
    <div className="orion-modal-backdrop fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-6 backdrop-blur-md animate-in fade-in duration-300">
      <div
        className="absolute inset-0"
        onClick={!isSubmitting ? onClose : undefined}
      ></div>

      <div className="orion-modal-panel relative w-full max-w-3xl rounded-2xl sm:rounded-[3rem] shadow-2xl p-5 sm:p-10 border animate-in zoom-in-95 max-h-[90vh] overflow-y-auto custom-scrollbar">
        <h3 className="text-xl sm:text-2xl font-black mb-2 dark:text-white tracking-tight">Nouveau Prospect</h3>
        <p className="text-slate-500 dark:text-slate-400 mb-6 sm:mb-8 text-sm">Créez une nouvelle opportunité commerciale</p>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <p className="text-[11px] text-slate-500 dark:text-slate-400 -mt-2">
            Renseignez au moins <strong>le nom complet</strong> ou <strong>l'entreprise</strong>.
          </p>

          <div>
            <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
              Client (entreprise)
            </label>
            <input
              type="text"
              value={formData.clientCompanyName}
              onChange={e => setFormData({...formData, clientCompanyName: e.target.value})}
              placeholder="ex: ACME Industries"
              className="orion-modal-input w-full border rounded-2xl px-5 py-4 text-sm font-bold focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
              Nom complet
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              placeholder="ex: Jean Dupont"
              className="orion-modal-input w-full border rounded-2xl px-5 py-4 text-sm font-bold focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
              disabled={isSubmitting}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
                Tag
              </label>
              <input
                type="text"
                value={formData.tag}
                onChange={e => setFormData({ ...formData, tag: e.target.value })}
                placeholder="ex: Prioritaire"
                className="orion-modal-input w-full border rounded-2xl px-5 py-4 text-sm font-bold focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
                Secteur d'activite
              </label>
              <select
                title="Secteur d'activité"
                value={formData.sectorActivity}
                onChange={e => setFormData({ ...formData, sectorActivity: e.target.value })}
                className="orion-modal-input w-full border rounded-2xl px-5 py-4 text-sm font-bold focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                disabled={isSubmitting}
              >
                <option value="">Selectionner</option>
                {sectors.map(sector => (
                  <option key={sector} value={sector}>
                    {sector}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={e => setFormData({...formData, email: e.target.value})}
                placeholder="jean@client.com"
                className="orion-modal-input w-full border rounded-2xl px-5 py-4 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
                Téléphone mobile
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={e => setFormData({...formData, phone: e.target.value})}
                placeholder="+33 6 12 34 56 78"
                className="orion-modal-input w-full border rounded-2xl px-5 py-4 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
                Téléphone fixe
              </label>
              <input
                type="tel"
                value={formData.phoneLandline}
                onChange={e => setFormData({...formData, phoneLandline: e.target.value})}
                placeholder="+33 1 23 45 67 89"
                className="orion-modal-input w-full border rounded-2xl px-5 py-4 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
                Revenu potentiel
              </label>
              <div className="flex flex-wrap gap-2">
                {effectiveBrackets.map(bracket => (
                  <button
                    key={bracket}
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => {
                      setFormData({ ...formData, potentialRevenue: formData.potentialRevenue === bracket ? '' : bracket });
                      setShowCustomRevenue(false);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                      formData.potentialRevenue === bracket
                        ? 'orion-revenue-chip-active'
                        : 'orion-revenue-chip-muted'
                    }`}
                  >
                    {bracket}
                  </button>
                ))}
                {/* Chip "Autre montant" */}
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => {
                    setShowCustomRevenue(!showCustomRevenue);
                    if (!showCustomRevenue && effectiveBrackets.includes(formData.potentialRevenue)) {
                      setFormData({ ...formData, potentialRevenue: '' });
                    }
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                    showCustomRevenue || (!effectiveBrackets.includes(formData.potentialRevenue) && formData.potentialRevenue)
                      ? 'orion-revenue-chip-active'
                      : 'orion-revenue-chip-muted'
                  }`}
                >
                  {!effectiveBrackets.includes(formData.potentialRevenue) && formData.potentialRevenue
                    ? `✎ ${formData.potentialRevenue}`
                    : 'Autre montant…'}
                </button>
              </div>
              {showCustomRevenue && (
                <div className="mt-3 animate-in slide-in-from-top-1 duration-150">
                  <input
                    type="text"
                    value={
                      !effectiveBrackets.includes(formData.potentialRevenue) ? formData.potentialRevenue : ''
                    }
                    onChange={e => setFormData({ ...formData, potentialRevenue: e.target.value })}
                    placeholder="ex: 75 000 €, Environ 200K…"
                    className="orion-modal-input w-full border rounded-2xl px-5 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                    disabled={isSubmitting}
                    autoFocus
                  />
                </div>
              )}
            </div>
          </div>

          <p className="text-[11px] text-slate-500 dark:text-slate-400 -mb-2">
            Source, note initiale et champs personnalisés sont renseignables ensuite depuis la fiche prospect.
          </p>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting} 
              className="orion-modal-ghost-btn flex-1 py-4 text-xs font-black uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
            <button 
              type="submit" 
              disabled={isSubmitting} 
              className="orion-modal-primary-btn flex-1 py-4 text-white rounded-2xl text-xs font-black shadow-xl uppercase tracking-widest transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Création...</span>
                </>
              ) : (
                'Créer le prospect'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      {duplicatePrompt && (
        <ConfirmModal
          variant="warning"
          title="Doublon détecté"
          message={`Un prospect avec le même email ou téléphone existe déjà :\n${duplicatePrompt.info}\n\nVoulez-vous quand même créer ce nouveau prospect ?`}
          confirmLabel="Créer quand même"
          cancelLabel="Annuler"
          busy={duplicateSaving}
          onConfirm={confirmDuplicate}
          onCancel={() => setDuplicatePrompt(null)}
        />
      )}
    </>
  );
};

export default AddLeadModal;
